import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Seo from "@/components/Seo";
import { ArrowRight, Check, CheckCircle2 } from "lucide-react";
import { FadeUp } from "@/lib/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { t: "Make your facts identical everywhere", d: "Name, services, areas, hours and phone — matched exactly on your website, Google Business Profile and every listing." },
  { t: "Say what you do in plain sentences", d: "Engines quote sentences, not slogans. State what you do, for whom and where in the first paragraph." },
  { t: "Shape pages as questions with answers", d: "Use the headings customers actually ask — and answer directly in the first two sentences." },
  { t: "Stack the proof", d: "A complete profile, a steady stream of honest reviews, real job photos, mentions from other sites." },
  { t: "Let the machines read", d: "Unblock AI crawlers in robots.txt and keep an llms.txt describing your business in plain text." },
  { t: "Audit yourself monthly", d: "Ask each engine who's a good trade in your town — note who gets named and fix the source of the gap." },
  { t: "Keep it fresh", d: "New reviews, updated pages, seasonal answers. AI visibility is a routine, not a switch." },
];

export default function AiAnswerChecklistPage() {
  const [done, setDone] = useState(() => new Set());
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const toggle = (i) =>
    setDone((prev) => {
      const next = new Set(prev);
      next.has(i) ? next.delete(i) : next.add(i);
      return next;
    });

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    setError("");
    try {
      await axios.post(`${API}/checklist/email`, { email });
      setSent(true);
      setEmail("");
    } catch (err) {
      setError(
        err.response?.data?.detail || "Something went wrong — please try again."
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="ai-checklist-page" className="grain bg-white">
      <Seo
        title="AI Answer Checklist — 7 Steps to Get Named by AI"
        description="The shareable 7-point checklist for showing up in ChatGPT, Gemini and Claude answers: identical facts, plain sentences, proof, open crawlers, a monthly audit."
        path="/ai-answer-checklist"
      />
      <section className="relative overflow-hidden pb-10 pt-40 md:pt-48">
        <div className="pointer-events-none absolute -right-40 top-0 h-[420px] w-[420px] rounded-full bg-flame/10 blur-[140px]" />
        <div className="relative mx-auto max-w-3xl px-4 sm:px-8">
          <FadeUp>
            <p className="font-mono text-[13px] font-semibold uppercase tracking-[0.2em] text-flame">
              The checklist · Share it with your team
            </p>
            <h1 className="mt-5 font-display text-4xl font-extrabold leading-[1.0] tracking-tight text-slate-900 sm:text-5xl">
              Show up in AI answers<span className="text-flame">.</span>
            </h1>
            <p className="mt-6 text-lg leading-relaxed text-mist">
              Seven things that decide whether ChatGPT, Gemini and Claude name
              your business. Tick them off — then keep them that way.
            </p>
            <div
              data-testid="ai-checklist-progress"
              className="mt-6 font-mono text-[13px] uppercase tracking-[0.18em] text-faint"
            >
              {done.size} of {STEPS.length} done
            </div>
          </FadeUp>
        </div>
      </section>

      <section className="border-t border-slate-200 py-14 md:py-20">
        <div className="mx-auto max-w-3xl space-y-4 px-4 sm:px-8">
          {STEPS.map((s, i) => (
            <FadeUp key={s.t}>
              <button
                type="button"
                data-testid={`ai-checklist-item-${i + 1}`}
                onClick={() => toggle(i)}
                className={`flex w-full items-start gap-4 rounded-2xl border p-6 text-left transition-all duration-200 ${
                  done.has(i)
                    ? "border-flame/60 bg-flame/5"
                    : "border-slate-200 bg-white hover:border-flame/40"
                }`}
              >
                <span
                  className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md border transition-colors ${
                    done.has(i)
                      ? "border-flame bg-flame text-white"
                      : "border-slate-300 bg-white text-transparent"
                  }`}
                >
                  <Check className="h-4 w-4" />
                </span>
                <span>
                  <span className="block font-display text-lg font-bold text-slate-900">
                    {i + 1}. {s.t}
                  </span>
                  <span className="mt-1 block text-base leading-relaxed text-mist">
                    {s.d}
                  </span>
                </span>
              </button>
            </FadeUp>
          ))}
        </div>
      </section>

      <section
        data-testid="checklist-email-section"
        className="border-t border-slate-200 py-16 md:py-20"
      >
        <div className="mx-auto max-w-3xl px-4 sm:px-8">
          <FadeUp>
            <div className="rounded-3xl border border-slate-200 bg-[#F8F9FA] p-8 text-center md:p-12">
              {sent ? (
                <div data-testid="checklist-email-success">
                  <CheckCircle2 className="mx-auto h-10 w-10 text-flame" />
                  <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-slate-900">
                    Sent — check your inbox<span className="text-flame">.</span>
                  </h2>
                  <p className="mx-auto mt-4 max-w-md text-lg leading-relaxed text-mist">
                    The seven steps are on their way. If they don't show up in a
                    few minutes, have a quick look in spam.
                  </p>
                  <button
                    type="button"
                    data-testid="checklist-email-again-button"
                    onClick={() => setSent(false)}
                    className="mt-6 font-mono text-base font-semibold uppercase tracking-[0.15em] text-flame underline underline-offset-4"
                  >
                    Send to a different email
                  </button>
                </div>
              ) : (
                <>
                  <h2 className="font-display text-3xl font-bold tracking-tight text-slate-900">
                    Want this checklist in your inbox<span className="text-flame">?</span>
                  </h2>
                  <p className="mx-auto mt-4 max-w-xl text-lg leading-relaxed text-mist">
                    Enter your email and we'll send the seven steps over —
                    handy to keep, easy to share with your team.
                  </p>
                  <form
                    data-testid="checklist-email-form"
                    onSubmit={submit}
                    className="mx-auto mt-8 flex max-w-xl flex-col gap-3 sm:flex-row"
                  >
                    <input
                      data-testid="checklist-email-input"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@yourbusiness.com"
                      aria-label="Your email address"
                      className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-lg text-slate-900 placeholder:text-faint outline-none transition-colors focus:border-flame"
                    />
                    <button
                      data-testid="checklist-email-submit"
                      type="submit"
                      disabled={sending}
                      className="shrink-0 rounded-2xl bg-flame px-6 py-3 font-mono text-base font-semibold uppercase tracking-[0.15em] text-obsidian transition-all duration-300 hover:shadow-[0_0_28px_rgba(255,85,0,0.45)] disabled:opacity-60"
                    >
                      {sending ? "Sending…" : "Email me the checklist"}
                    </button>
                  </form>
                  {error ? (
                    <p
                      data-testid="checklist-email-error"
                      className="mt-4 text-base font-semibold text-flame"
                    >
                      {error}
                    </p>
                  ) : null}
                  <p
                    data-testid="checklist-optin-note"
                    className="mt-5 text-[11px] leading-relaxed text-faint"
                  >
                    By submitting, you're opting in to learn more about AI with
                    ZD Design. One email, no spam — unsubscribe any time.
                  </p>
                </>
              )}
            </div>
          </FadeUp>
        </div>
      </section>

      <section className="border-t border-slate-200 py-16 md:py-20">
        <div className="mx-auto max-w-3xl px-4 text-center sm:px-8">
          <FadeUp>
            <h2 className="font-display text-3xl font-bold tracking-tight text-slate-900">
              Want it handled for you<span className="text-flame">?</span>
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-lg leading-relaxed text-mist">
              Our SEO + AI Visibility program runs this routine every month —
              one offer, one person accountable.
            </p>
            <Link
              to="/ai-visibility"
              data-testid="ai-checklist-cta"
              className="mt-8 inline-flex items-center gap-2 rounded-2xl bg-flame px-8 py-4 font-mono text-base font-semibold uppercase tracking-[0.15em] text-obsidian transition-all duration-300 hover:shadow-[0_0_40px_rgba(255,85,0,0.5)]"
            >
              See the program
              <ArrowRight className="h-4 w-4" />
            </Link>
            <p className="mt-6 text-base text-faint">
              New here? Read{" "}
              <Link
                to="/blog/what-is-geo-how-does-it-work"
                className="font-semibold text-flame underline underline-offset-4"
              >
                what GEO is
              </Link>{" "}
              or{" "}
              <Link
                to="/blog/how-to-show-up-in-ai-answers"
                className="font-semibold text-flame underline underline-offset-4"
              >
                the full step-by-step
              </Link>
              .
            </p>
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
